import type { Dispatch, SetStateAction } from "react";

import { swSettings } from "app/common/services/swSettings";
import type { IDashboardExtended } from "app/components/dashboard/services/IDashboard";
import type {
    ISharingModalTextContainer,
    ISharingModalState,
    IUpdateSharingStateRequest,
} from "app/components/SharingModal/types";
import {
    ISharingModalOption,
    ISharingModalOptionName,
    ISharingModalShareType,
} from "app/components/SharingModal/types";
import { translate } from "app/filters/i18nFilter";
import type {
    SharingDashboardFailedResponse,
    SharingDashboardSuccessResponse,
} from "app/pages/dashboard/ShareDashboardService";
import { updateSharingState } from "app/pages/dashboard/ShareDashboardService";

const mappingSharingState = {
    [ISharingModalOption.SHARED]: ISharingModalOptionName.SHARED,
    [ISharingModalOption.RESTRICTED]: ISharingModalOptionName.RESTRICTED,
};

const sharingOptionsTextTranslation = {
    [ISharingModalOption.SHARED]: {
        title: "dashboard.shareDashboard.modal.options.shared.title",
        subtitle: "dashboard.shareDashboard.modal.options.shared.subtitle",
    },
    [ISharingModalOption.RESTRICTED]: {
        title: "sharing.asset.modal.options.users.title",
        subtitle: "dashboard.shareDashboard.modal.options.restricted.subtitle",
    },
};

export const resolveSharingStateToShareModalFormat = (record: IDashboardExtended): ISharingModalOption => {
    const { targetType } = record;
    const modalSharingState = Object.keys(mappingSharingState).find(
        (key) => mappingSharingState[Number(key) as ISharingModalOption] === targetType,
    );

    return Number(modalSharingState) as ISharingModalOption;
};

const resolveSharingStateToRequiredFormat = (shareType: ISharingModalOption): ISharingModalOptionName => {
    return mappingSharingState[shareType];
};

export const getInitialSharingState =
    (
        initialSharingState: ISharingModalOption,
        initialSharedWithUsers: number[],
        modalText: ISharingModalTextContainer,
        sharedWithInvitations: number[],
    ) =>
    (): ISharingModalState => {
        return {
            sharingState: initialSharingState,
            sharedWithUsers: initialSharedWithUsers,
            notification:
                modalText.sharingOptionTextContainer[ISharingModalOption.RESTRICTED].notificationDefaultContent,
            sharedWithUsersToShow: [...initialSharedWithUsers, ...sharedWithInvitations],
            sharedWithInvitations,
            notifyUserIds: [],
        };
    };

export const getModalText = (
    dashboard: IDashboardExtended,
    headerTitleKey = "dashboard.shareDashboard.modal.title",
    notificationKey = "dashboard.shareDashboard.modal.options.users.subtitle.notification.default.content",
): ISharingModalTextContainer => {
    const { title } = dashboard;

    return {
        header: {
            title: translate(headerTitleKey),
            subtitle: title,
        },
        sharingOptionTextContainer: {
            [ISharingModalOption.SHARED]: {
                optionIndex: ISharingModalOption.SHARED,
                title: translate(sharingOptionsTextTranslation[ISharingModalOption.SHARED].title),
                subtitle: translate(sharingOptionsTextTranslation[ISharingModalOption.SHARED].subtitle),
            },
            [ISharingModalOption.RESTRICTED]: {
                optionIndex: ISharingModalOption.RESTRICTED,
                title: translate(sharingOptionsTextTranslation[ISharingModalOption.RESTRICTED].title),
                subtitle: translate(sharingOptionsTextTranslation[ISharingModalOption.RESTRICTED].subtitle),
                notificationDefaultContent: translate(notificationKey, {
                    domainAndSegmentNameText: title,
                    waveIcon: "👋",
                }),
            },
        },
    };
};

export const onSharingSubmit =
    (
        dashboard: IDashboardExtended,
        onUpdateDashboard: (customSegment: IDashboardExtended) => void,
        dispatchSuccessToast: (text: string) => void,
        dispatchErrorToast: (text: string) => void,
        setIsLoading: Dispatch<SetStateAction<boolean>>,
        shareEndPoint: string,
    ) =>
    (sharing: ISharingModalState, onClose?: () => void) => {
        const {
            sharingState: newSharingState,
            sharedWithUsers: newUsersToShareWith = [],
            sharedWithInvitations: invitationsToShareWith = [],
            notification = "",
            notifyUserIds = [],
        } = sharing;

        const dashboardSharingType = getDashboardSharingType(sharing);

        const onUpdateSharingState = async () => {
            setIsLoading(true);

            let request: IUpdateSharingStateRequest = {
                dashboardId: dashboard.id,
                newSharingState,
                sharedWithInvitations: [],
                notification,
                sharedWithAccounts: [],
                sharedWithUsers: [],
                notifyUserIds,
                shareEndPoint,
            };

            switch (dashboardSharingType) {
                case ISharingModalShareType.OVERWRITE_SHARING: {
                    const sharedWithAccountsForOverwriteSharing =
                        newSharingState === ISharingModalOption.SHARED ? [swSettings.user.accountId] : [];
                    const sharedWithUsersForOverwriteSharing =
                        newSharingState === ISharingModalOption.RESTRICTED ? newUsersToShareWith : [];
                    const sharedWithInvitationsForOverwriteSharing =
                        newSharingState === ISharingModalOption.RESTRICTED ? invitationsToShareWith : [];

                    request = {
                        ...request,
                        sharedWithAccounts: sharedWithAccountsForOverwriteSharing,
                        sharedWithUsers: sharedWithUsersForOverwriteSharing,
                        sharedWithInvitations: sharedWithInvitationsForOverwriteSharing,
                    };

                    break;
                }
                case ISharingModalShareType.NOTIFY: {
                    request = {
                        ...request,
                        sharedWithAccounts: [swSettings.user.accountId],
                        sharedWithUsers: newUsersToShareWith,
                        sharedWithInvitations: invitationsToShareWith,
                    };

                    break;
                }
            }

            try {
                const response = await updateSharingState(request);

                if (
                    (typeof response === "boolean" && response) ||
                    (response as SharingDashboardSuccessResponse).result
                ) {
                    const updatedDashboard = {
                        ...dashboard,
                        sharedWithUsers:
                            typeof response === "boolean" ? [] : (response as SharingDashboardSuccessResponse).userIds,
                        sharedWithAccounts:
                            typeof response === "boolean"
                                ? []
                                : (response as SharingDashboardSuccessResponse).accountIds,
                        targetType: resolveSharingStateToRequiredFormat(newSharingState),
                        sharedWithInvitations:
                            typeof response === "boolean"
                                ? []
                                : (response as SharingDashboardSuccessResponse).invitationIds,
                    };
                    onUpdateDashboard(updatedDashboard);
                    dispatchSuccessToast(translate("segment.sharing.success.toast"));
                } else if (
                    !(response as SharingDashboardFailedResponse).result &&
                    !(response as SharingDashboardFailedResponse).failedInvites?.length
                ) {
                    dispatchErrorToast(translate("segment.sharing.fail.toast"));
                } else {
                    dispatchErrorToast(translate("dashboard.shareDashboard.toast.failedEmails"));
                }
            } catch {
                dispatchErrorToast(translate("segment.sharing.fail.toast"));
            }

            setIsLoading(false);
            onClose && onClose();
        };
        onUpdateSharingState();
    };

export const processDashboard = (dashboard: FixmeType): IDashboardExtended => {
    return {
        ...dashboard,
        sharedWithAccounts: dashboard.sharedWithAccounts
            ? dashboard?.sharedWithAccounts.map((accountId: string | number) => Number(accountId))
            : [],
        sharedWithUsers: dashboard?.sharedWithUsers.map((userId: string) => Number(userId)),
        targetType: !dashboard.sharedWithAccounts.length
            ? ISharingModalOptionName.RESTRICTED
            : ISharingModalOptionName.SHARED,
        sharedWithInvitations: dashboard.sharedWithInvitations?.map((userId: string) => Number(userId)) || [],
    };
};

const getDashboardSharingType = (sharing: ISharingModalState): ISharingModalShareType => {
    const { sharingState: newSharingState } = sharing;
    const isNotifying = newSharingState === ISharingModalOption.SHARED;

    if (isNotifying) {
        return ISharingModalShareType.NOTIFY;
    }

    return ISharingModalShareType.OVERWRITE_SHARING;
};
